<template>
	<div class="header-wrapper" :class="{ publishing: isPublishing }">
		<div class="left-items">
			<div class="devices">
				<div
					class="header-btn leave"
					@click="showLeaveMeetingPopup = true"
					@keyup.enter="showLeaveMeetingPopup = true"
					:aria-label="$t('meetings_button_endbroadcast_leave')"
					tabindex="0"
					role="button"
				>
					{{ $t('meetings_button_endbroadcast_leave') }}
				</div>

				<div
					class="header-btn broadcasting start"
					:class="{ 'start-disabled': overLimit }"
					v-if="!isPublishing"
					@click="onClickStartBroadcasting()"
					@keyup.enter="onClickStartBroadcasting()"
					:aria-label="$t('meetings_button_startbroadcast')"
					tabindex="0"
					role="button"
				>
					{{ $t('meetings_button_startbroadcast') }}
				</div>

				<div
					class="header-btn broadcasting stop"
					:title="$t('meetings_button_switchToObserver')"
					v-if="isPublishing && hasMultipleStreams"
					@click="onClickStopBroadcasting()"
					@keyup.enter="onClickStopBroadcasting()"
					:aria-label="$t('meetings_button_switchToObserver')"
					tabindex="0"
					role="button"
				>
					{{ $t('meetings_button_switchToObserver') }}
				</div>

				<div
					v-if="publishingAllowed && isPublishing"
					class="header-btn share-screen"
					:title="shareScreenText"
					:class="{
						active: screenSharing,
					}"
					@click="pubScreenClick"
					@keyup.enter="pubScreenClick"
					:aria-label="$t('meetings_share_screen')"
					tabindex="0"
					role="button"
				>
					<DesktopIcon :class="{ active: screenSharing }" />
					<div class="button-text">
						{{ shareScreenText }}
					</div>
				</div>

				<MediaInputDevices
					v-if="publishingAllowed && isPublishing"
					type="audio"
				/>

				<MediaInputDevices
					v-if="publishingAllowed && isPublishing"
					type="video"
				/>

				<div
					v-if="orgCaptionsEnabled"
					class="header-btn captions"
					:title="$t('meetings_captions_label')"
					:class="{
						active: isCaptionsActive,
					}"
					@click="onClickCaptions"
					@keyup.enter="onClickCaptions"
					:aria-label="$t('meetings_share_screen')"
					tabindex="0"
					role="button"
				>
					<ClosedCaptioningIcon type="solid" />
					<div class="button-text" v-text="$t('meetings_captions_label')" />
				</div>
			</div>
		</div>

		<div class="right-items">
			<div
				class="header-btn fullscreen"
				:title="$t('meetings_full_screen')"
				@click="$emit('fullscreen')"
				@keyup.enter="$emit('fullscreen')"
				v-if="fullscreenEnabled"
				:aria-label="$t('meetings_full_screen')"
				tabindex="0"
				role="button"
			>
				<FullscreenIcon :class="{ active: isFullscreen }" />
			</div>

			<RecordButton :isPublishing="isPublishing" />

			<div class="vertical-divider" v-if="hasMultipleStreams" tabindex="-1" />

			<div class="layout-selection" v-if="hasMultipleStreams">
				<ActiveSpeakerIcon
					class="active-speaker-layout"
					:active="activeSpeakerSelected"
					@click="changeView('active_speaker')"
					@keyup.enter="changeView('active_speaker')"
					:aria-label="$t('meetings_active_speaker_view')"
					tabindex="0"
					role="button"
				/>
				<GalleryIcon
					class="gallery-layout"
					:active="gallerySelected"
					@click="changeView('gallery')"
					@keyup.enter="changeView('gallery')"
					:aria-label="$t('meetings_gallery_view')"
					tabindex="0"
					role="button"
				/>
			</div>

			<SidebarTabs
				:open="isSidebarOpen"
				@set-open="$emit('set-sidebar-open', $event)"
			/>

			<JoinMeetingPopup
				v-if="showJoinMeetingPopup"
				@broadcast="startPublishing"
				@close="showJoinMeetingPopup = false"
			/>

			<LeaveMeetingPopup
				v-if="showLeaveMeetingPopup"
				@stop-videosession="stopVideoSession()"
				@stop-broadcasting="stopPublishing()"
				@leave-meeting="leaveMeeting()"
				@close="showLeaveMeetingPopup = false"
			/>
		</div>
	</div>
</template>

<script>
import { getOrganization } from '@/store'
import screenfull from 'screenfull'
import MediaInputDevices from './MediaInputDevices'
import RecordButton from './RecordButton'
import {
	ActiveSpeakerIcon,
	GalleryIcon,
	DesktopIcon,
	FullscreenIcon,
	ClosedCaptioningIcon,
} from '@/icons'
import { SidebarTabs } from './Sidebar'
import JoinMeetingPopup from './JoinMeetingPopup'
import LeaveMeetingPopup from './LeaveMeetingPopup'
import store, {
	setPublishing,
	getIsPublishing,
	setSharing,
	getIsSharing,
	getStreamsForDisplay,
	isCaptionsActive,
	toggleCaptionsIsActive,
	isOverLimit,
} from './data'

export default {
	name: 'Header',
	props: ['view', 'isSidebarOpen', 'isFullscreen'],
	emits: ['fullscreen', 'set-sidebar-open', 'change-view'],
	components: {
		SidebarTabs,
		MediaInputDevices,
		RecordButton,
		ActiveSpeakerIcon,
		GalleryIcon,
		JoinMeetingPopup,
		LeaveMeetingPopup,
		DesktopIcon,
		FullscreenIcon,
		ClosedCaptioningIcon,
	},

	data() {
		const org = getOrganization()
		return {
			store,
			publishingAllowed: false,
			showJoinMeetingPopup: false,
			showLeaveMeetingPopup: false,
			orgCaptionsEnabled: org.meetings_captions_enabled,
			isCaptionsActive: isCaptionsActive(),
			isPublishing: getIsPublishing(),
			screenSharing: getIsSharing(),
			hasMultipleStreams: getStreamsForDisplay().length > 1,
		}
	},

	created() {
		this.$onEventBus({
			publisherAccessAllowed: (bool) => {
				if (bool) {
					this.publishingAllowed = true
				}
			},
			publisherAdded: () => {
				Object.assign(this, {
					isPublishing: getIsPublishing(),
					screenSharing: getIsSharing(),
				})
			},
			streamUpdate: () => {
				Object.assign(this, {
					isPublishing: getIsPublishing(),
					screenSharing: getIsSharing(),
					hasMultipleStreams: getStreamsForDisplay().length > 1,
				})
			},
		})
	},

	computed: {
		activeSpeakerSelected() {
			return this.view === 'active_speaker'
		},

		gallerySelected() {
			return this.view === 'gallery'
		},

		// isPublishing() {
		// 	return getIsPublishing()
		// },

		// screenSharing() {
		// 	return getIsSharing()
		// },

		shareScreenText() {
			return this.screenSharing
				? this.$t('meetings_stop_sharing_screen')
				: this.$t('meetings_share_screen')
		},

		// hasMultipleStreams() {
		// 	return getStreamsForDisplay().length > 1
		// },

		overLimit() {
			return isOverLimit()
		},

		fullscreenEnabled() {
			return screenfull.isEnabled
		},
	},

	methods: {
		changeView(view) {
			this.$emit('change-view', view)
		},

		startPublishing() {
			this.showJoinMeetingPopup = false
			setPublishing(true)
		},

		stopPublishing() {
			this.showLeaveMeetingPopup = false
			EventBus.$emit('stopPublishing', 'video')

			//check for screen sharing and tear it down too
			if (getIsSharing()) {
				EventBus.$emit('stopPublishing', 'screen')
			}
		},

		pubScreenClick() {
			if (getIsSharing()) {
				EventBus.$emit('stopPublishing', 'screen')
			} else {
				setSharing(true)
			}
		},

		stopVideoSession() {
			this.showLeaveMeetingPopup = false
			EventBus.$emit('stopVideoSession')
		},

		leaveMeeting() {
			this.showLeaveMeetingPopup = false
			EventBus.$emit('leaveMeeting')
		},

		onClickStartBroadcasting() {
			if (isOverLimit()) {
				return EventBus.$emit('showOverLimitWarning')
			}
			this.showJoinMeetingPopup = true
		},

		onClickStopBroadcasting() {
			this.stopPublishing()
		},

		onClickCaptions() {
			if (!this.orgCaptionsEnabled) return
			this.isCaptionsActive = toggleCaptionsIsActive()
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

$red: #ff525a;
$blue: #4a91ff;
$btn-height: 32px;
$min-width: 720px;
$min-width-publishing: 1038px;
$collapse-width: 1333px;

.header-btn,
:deep(.header-btn) {
	height: $btn-height;
	padding: 0 16px;
	display: flex;
	align-items: center;
	border-radius: 4px;
	flex-shrink: 0;
	font-size: 14px;
	font-family: Avenir-Medium;
	color: #bcbcbc;
	background-color: #38393c;
	cursor: pointer;
	text-transform: capitalize;
	outline: none;
}

.header-btn.captions {
	gap: 8px;
	&.active {
		color: var(--teal-300);
	}
	.pronto-icon {
		font-size: 18px;
	}
}

.header-btn svg {
	margin-right: 8px;
}

:deep(.device-button-wrapper) {
	@extend .header-btn;
}

:deep(.record-button) {
	@extend .header-btn;
}

.header-container {
	display: flex;
	align-items: center;
	justify-content: space-between;
}

.header-wrapper {
	@include flex-spread;
	min-width: $min-width;
	&.publishing {
		min-width: $min-width-publishing;
		@media screen and (max-width: $collapse-width) {
			.device-button {
				min-width: 75px;
			}
		}
		@media screen and (max-width: $collapse-width) {
			:deep(.button-text) {
				display: none;
			}
			:deep(.header-btn) svg {
				margin-right: 0 !important;
			}
		}
	}
}

.left-items {
	@include flex(8px);
}

.right-items {
	@include flex(8px);
}

.devices {
	@include flex(8px);
}

.devices div {
	-webkit-touch-callout: none;
	-webkit-user-select: none;
	-khtml-user-select: none;
	-moz-user-select: none;
	-ms-user-select: none;
	user-select: none;
	user-drag: none;
}

.layout-selection {
	height: $btn-height;
	margin: 0 8px 0 0;
	min-width: 62px;
	display: flex;
	align-items: center;
}

.active-speaker-layout,
.gallery-layout {
	width: 26px;
	height: 26px;
	cursor: pointer;
	outline: none;
}

.active-speaker-layout {
	margin-right: 5px;
}

.vertical-divider {
	border-left: 1px solid #414141;
	height: 26px;
	margin: 0 8px 0 0;
}

.leave {
	color: #ffffff;
	background-color: $red;
	font-family: Avenir-Medium;
	cursor: pointer;
}

.broadcasting {
	min-width: 60px;
	color: #ffffff;
	font-family: Avenir-Medium;
	cursor: pointer;
}

.start {
	color: white;
	background-color: $blue;
}

.start-disabled {
	opacity: 0.6;
}

.stop {
	color: white;
	background-color: #797d86;
	min-width: 156px;
}

.share-screen {
	&.active {
		color: var(--teal-300);
		:deep(svg) g {
			fill: var(--teal-300);
		}
	}
}

.fullscreen {
	padding: 0 10px;
	:deep(svg) {
		margin-right: 0;
		g {
			fill: #bcbcbc;
		}
	}
}
</style>
